import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../context/SupabaseClient';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [challengeId, setChallengeId] = useState(null);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [needsMfa, setNeedsMfa] = useState(false);
  const [factorId, setFactorId] = useState(null);

  const navigate = useNavigate();
  const { updatePassword } = useAuth();

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const errorDescription = hashParams.get('error_description');

    if (errorDescription) {
      setError(errorDescription.replace(/\+/g, ' '));
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setIsReady(true);
      else setError('Your reset link is invalid or has expired.');
    });
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (needsMfa) {
        // Phase 2: verify using REAL ids
        const { error: mfaError } = await supabase.auth.mfa.verify({
          factorId,
          challengeId,
          code: mfaCode,
        });
        if (mfaError) throw new Error('Invalid MFA code.');

        await updatePassword(password);
        navigate('/');
      } else {
        // Phase 1: attempt password update
        if (password.length < 8) throw new Error('Password must be at least 8 characters.');
        if (password !== confirmPassword) throw new Error('Passwords do not match.');

        try {
          await updatePassword(password);
          navigate('/');
        } catch (err) {
          if (err.message === 'AAL2_REQUIRED') {
            // Get the user's real MFA factor
            const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();
            if (factorsError) throw factorsError;

            const totpFactor = factorsData.totp[0];
            if (!totpFactor) throw new Error('No MFA factor found for this account.');

            // Create a real challenge for that factor
            const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
              factorId: totpFactor.id,
            });
            if (challengeError) throw challengeError;

            setFactorId(totpFactor.id);
            setChallengeId(challengeData.id);
            setNeedsMfa(true);
            setError('MFA authentication required to proceed.');
            return;
          }
          throw err;
        }
      }
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.shell}>
      <div style={styles.card}>
        <img src="/images/icon.png" alt="Logo" className="auth-logo" />
        <div style={styles.eyebrow}>{needsMfa ? 'VERIFY IDENTITY' : 'RESET PASSWORD'}</div>
        <h1 style={styles.title}>{needsMfa ? 'Enter Auth Code' : 'Set new password'}</h1>

        {!isReady ? (
          <p style={styles.error}>{error || 'Verifying link...'}</p>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            {!needsMfa ? (
              <>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>New password</label>
                  <input style={styles.input} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Confirm password</label>
                  <input style={styles.input} type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required placeholder="••••••••" />
                </div>
              </>
            ) : (
              <div style={styles.fieldGroup}>
                <label style={styles.label}>6-Digit Authenticator Code</label>
                <input style={styles.input} type="text" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} required placeholder="000000" maxLength="6" />
              </div>
            )}

            {error && <p style={styles.error}>{error}</p>}

            <button style={styles.button} type="submit" disabled={loading}>
              {loading ? 'Processing...' : (needsMfa ? 'Verify & Update' : 'Update password')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  shell: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', padding: '20px' },
  card: { width: '100%', maxWidth: '400px', background: '#fff', padding: '40px', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
  eyebrow: { textAlign: 'center', fontSize: '12px', letterSpacing: '1px', color: '#666', marginBottom: '8px' },
  title: { textAlign: 'center', fontSize: '24px', marginBottom: '24px', color: '#333' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '14px', fontWeight: '600', color: '#333' },
  input: { padding: '12px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '16px' },
  button: { padding: '12px', borderRadius: '8px', border: 'none', background: '#667eea', color: '#fff', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', marginTop: '8px' },
  error: { color: '#d32f2f', textAlign: 'center', fontSize: '14px', margin: '0' }
};